import {Injectable} from '@angular/core';
import {
  Campaign,
  CampaignsFilter,
  CampaignType,
  Location,
  OrganizationType,
  Page,
  PageRequest,
  RestClientService,
  UserLocationService
} from 'shared-lib';
import {map, Observable} from 'rxjs';
import {SortOptions} from '../components/loyalty-layout/loyalty-layout.component';

@Injectable({
  providedIn: 'root'
})
export class CampaignService {

  constructor(private restClient: RestClientService, private userLocationService: UserLocationService) {
  }

  public getAllWithLoyaltyInRadius(pageRequest: PageRequest<any>, filter: CampaignsFilter): Observable<Page<Campaign>> {
    return this.restClient.getPageWithFilter<Campaign>('/campaigns/loyalty', pageRequest, {
      active: true,
      'with-loyalty': true,
      ...this.applyFilter(filter)
    }).pipe(map(page => {
      return {...page, content: page.content.map(this.enhanceOrganization)}
    }));
  }

  public getAllCampaignsWithLoyalty(filter: CampaignsFilter): Observable<Campaign[]> {
    return this.restClient.getListWithFilter<Campaign>('/campaigns/loyalty', {
      active: true,
      'with-loyalty': true,
      ...this.applyFilter(filter)
    });
  }

  public getAllCampaignsWithoutLoyalty(filter: CampaignsFilter): Observable<Campaign[]> {
    return this.restClient.getListWithFilter<Campaign>('/campaigns/loyalty', {
      active: true,
      'with-loyalty': false,
      ...this.applyFilter(filter)
    });
  }

  public getAllWithoutLoyaltyInRadius(pageRequest: PageRequest<any>, filter: CampaignsFilter): Observable<Page<Campaign>> {
    return this.restClient.getPageWithFilter<Campaign>('/campaigns/loyalty', pageRequest, {
      active: true,
      'with-loyalty': false,
      ...this.applyFilter(filter)
    }).pipe(map(page => {
      return {...page, content: page.content.map(this.enhanceOrganization)}
    }));
  }

  public getLimitedOfferCampaigns(filter: CampaignsFilter): Observable<Campaign[]> {
    return this.restClient.getPageWithFilter<Campaign>('/campaigns/loyalty', {page: 0, size: 5}, {
      active: true,
      'with-loyalty': true,
      ...this.applyFilter(filter)
    }).pipe(map(page => {
      return page.content.map(this.enhanceOrganization)
    }));
  }

  getLocationsForCampaign(campaignId: string): Observable<Location[]> {
    const location = this.userLocationService.getUserLocation();
    return this.restClient.getListWithFilter(`/campaigns/${campaignId}/locations`, {
      type: CampaignType.FREE_MONEY,
      latitude: location.latitude,
      longitude: location.longitude,
    });
  }

  //TODO most probably organization should be removed form campaing view.
  private enhanceOrganization(campaign: Campaign): Campaign {
    return {
      ...campaign, organization: {
        ...campaign.organization,
        isSponsor() {
          return campaign.organization!.type === OrganizationType.SPONSOR || campaign.organization!.type === OrganizationType.SPONSOR_WITH_MCARD;
        },
        isMerchant() {
          return campaign.organization!.type === OrganizationType.MERCHANT;
        }
      }
    }
  }

  private applyFilter(filter?: CampaignsFilter): LoyaltyCampaignFilter {
    let fmFilter = {
      sort: filter?.sort,
      amountFrom: filter?.amount?.min,
      amountTo: filter?.amount?.max,
    } as LoyaltyCampaignFilter;
    if (fmFilter.sort === SortOptions.NEAREST && this.userLocationService.getUserLocation()) {
      fmFilter = {...fmFilter, ...this.userLocationService.getUserLocation()};
    }
    if (filter?.geoBounds) {
      const geoBounds = filter.geoBounds;
      fmFilter = {
        ...fmFilter,
        southWestLat: geoBounds.southWest.latitude,
        southWestLng: geoBounds.southWest.longitude,
        northEastLat: geoBounds.northEast.latitude,
        northEastLng: geoBounds.northEast.longitude
      };
    }
    return fmFilter
  }

}

export enum LoyaltyScreen {
  WITH_LOYALTY = 'WITH_LOYALTY',
  WITHOUT_LOYALTY = 'WITHOUT_LOYALTY',
  BOUNTIES = 'BOUNTIES'
}

export interface LoyaltyCampaignFilter {
  sort?: SortOptions,
  merchantId?: string,
  amountFrom?: number,
  amountTo?: number,
  loyaltyScoreFrom?: number,
  loyaltyScoreTo?: number,
  latitude: number,
  longitude: number
  southWestLat?: number,
  southWestLng?: number,
  northEastLat?: number,
  northEastLng?: number,
}
